import { createContext, useState, useEffect, useMemo, useContext, useCallback } from 'react';

const STORAGE_KEY = 'cookie_consent';
const CONSENT_VERSION = '1.0';

export const CookieConsentContext = createContext(null);

/**
 * Cookie Consent Context Provider
 * Manages user consent preferences for cookies (GDPR compliance)
 */
export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);

        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed);
        }
      } catch {
        // Invalid stored data, clear it
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    setIsLoaded(true);
  }, []);

  /**
   * Save consent to state and localStorage
   */
  const saveConsent = useCallback((preferences) => {
    const consentData = {
      necessary: true, // Always true
      analytics: preferences.analytics || false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));
    setConsent(consentData);
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = useCallback(() => {
    saveConsent({ analytics: true });
  }, [saveConsent]);

  /**
   * Accept only necessary cookies
   */
  const acceptNecessary = useCallback(() => {
    saveConsent({ analytics: false });
  }, [saveConsent]);

  /**
   * Update consent with custom preferences
   */
  const updateConsent = useCallback(
    (preferences) => {
      saveConsent(preferences);
    },
    [saveConsent]
  );

  /**
   * Reset consent (for testing or user request)
   */
  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsent(null);
  }, []);

  // Check if user has made a consent choice
  const hasConsent = useMemo(() => {
    return consent !== null;
  }, [consent]);

  // Check if analytics cookies are allowed
  const analyticsAllowed = useMemo(() => {
    return consent?.analytics === true;
  }, [consent]);

  // Memoize context value
  const contextValue = useMemo(
    () => ({
      consent,
      isLoaded,
      hasConsent,
      analyticsAllowed,
      acceptAll,
      acceptNecessary,
      updateConsent,
      resetConsent,
    }),
    [consent, isLoaded, hasConsent, analyticsAllowed, acceptAll, acceptNecessary, updateConsent, resetConsent]
  );

  return <CookieConsentContext.Provider value={contextValue}>{children}</CookieConsentContext.Provider>;
};

/**
 * Hook to use cookie consent context
 */
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }

  return context;
};
